/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/MohSourcesColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Column definitions for MOH Sources DataGrid.
    Extracted from MOHPage for better modularity.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Badge } from '../../../components/ui/badge';
import { DataGridColumn } from '../../../hooks/useDataGrid';
import { MOHSource } from '../types';
import { formatDate } from '../utils';
import { useI18n } from '../../../hooks/useI18n';

/**
 * Get column definitions for MOH Sources DataGrid
 */
export function useMohSourcesColumns(): DataGridColumn<MOHSource>[] {
  const { t } = useI18n();

  return useMemo(
    () => [
      {
        key: 'name',
        label: t('mohSources.columns.name') || 'Name',
        visible: true,
        sortable: true,
        render: (_value: unknown, item: MOHSource) => (
          <span className="font-medium text-primary">{item?.name || '-'}</span>
        ),
      },
      {
        key: 'class_name',
        label: t('mohSources.columns.className') || 'Class',
        visible: true,
        sortable: true,
        render: (_value: unknown, item: MOHSource) => (
          <Badge variant="secondary" className="text-xs font-medium">
            {item?.class_name || 'default'}
          </Badge>
        ),
      },
      {
        key: 'source_type',
        label: t('mohSources.columns.sourceType') || 'Type',
        visible: true,
        sortable: true,
        render: (_value: unknown, item: MOHSource) => {
          const typeColors = {
            file: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
            stream: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
          };
          return (
            <Badge variant="outline" className={typeColors[item.source_type] || ''}>
              {item?.source_type || '-'}
            </Badge>
          );
        },
      },
      {
        key: 'enabled',
        label: t('mohSources.columns.enabled') || 'Status',
        visible: true,
        sortable: true,
        render: (_value: unknown, item: MOHSource) => (
          <Badge
            variant={item?.enabled ? 'default' : 'secondary'}
            className={
              item?.enabled
                ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
                : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20'
            }
          >
            {item?.enabled
              ? t('mohSources.status.enabled') || 'Enabled'
              : t('mohSources.status.disabled') || 'Disabled'}
          </Badge>
        ),
      },
      {
        key: 'created_at',
        label: t('mohSources.columns.createdAt') || 'Created',
        sortable: true,
        visible: false,
        render: (_value: unknown, item: MOHSource) =>
          formatDate((item as unknown as { createdAt?: string }).createdAt ?? item.created_at),
      },
      {
        key: 'actions',
        label: t('common:actions') || 'Actions',
        sortable: false,
        visible: true,
      },
    ],
    [t],
  );
}

